import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import SiteHeader from './marketing/SiteHeader';
import SiteFooter from './marketing/SiteFooter';
import HeroSection from './home/HeroSection';
import ContentSection from './home/ContentSection';
import RadarSection from './home/RadarSection';
import AudienceSection from './home/AudienceSection';
import PricingSection from './home/PricingSection';
import CtaBanner from './home/CtaBanner';
import { useSiteContent } from './home/siteContent';
import './home/home.css';
import useSeo, { ORGANIZATION_SCHEMA } from '../hooks/useSeo';
import AuthModal from './AuthModal';
import { Star } from 'lucide-react';

/*
 * Public homepage — built from the Figma "Home" frame (node 33779:2).
 * All copy and images are editable per language in Admin → Landingspagina;
 * defaults live in ./home/siteContent.js.
 */
const LandingPage = () => {
  const { user, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const { lang, cms, content } = useSiteContent();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register');

  const openAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Admin preview links: /?lang=fr#prijzen opens the page in that language at that section
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('lang');
    if (requested && requested !== lang) i18n.changeLanguage(requested);
  }, [location.search]);

  useEffect(() => {
    if (!location.hash || loading) return;
    const timer = setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(timer);
  }, [location.hash, loading, cms]);

  useSeo({
    path: '/',
    title: cms?.seo?.title || t('home.seo_title'),
    description: cms?.seo?.description || t('home.seo_description'),
    lang,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        ORGANIZATION_SCHEMA,
        {
          '@type': 'SoftwareApplication',
          name: 'WowProfit.io',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: 'Winst- en margeanalyse voor WooCommerce-webshops: nettowinst per product, land en periode, inclusief kosten, btw en AI-inzichten.',
          offers: {
            '@type': 'Offer',
            price: '9.95',
            priceCurrency: 'EUR',
            description: '30 dagen gratis proefperiode, daarna vanaf € 9,95 per maand',
          },
        },
      ],
    },
  });

  if (user && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand"></div>
      </div>
    );
  }

  const testimonials = cms?.testimonials;

  return (
    <div className="hp min-h-screen overflow-x-clip bg-white" data-testid="landing-page">
      <SiteHeader />

      <main>
        <HeroSection hero={content.hero} onAuth={openAuth} />
        <ContentSection intro={content.intro} blocks={content.blocks} features={content.features} onAuth={openAuth} />
        <RadarSection radar={content.radar} />
        <AudienceSection audience={content.audience} />
        <PricingSection pricing={content.pricing} onAuth={openAuth} />

        {/* Optional: only shown when testimonials are filled in the admin */}
        {testimonials?.items?.length > 0 && (
          <section id="reviews" className="hp-container mt-[clamp(56px,5.3333vw,96px)]" data-testid="testimonials-section">
            <div className="text-center">
              {testimonials.title && <h2 className="hp-h2-outfit text-brand-navy">{testimonials.title}</h2>}
              {testimonials.subtitle && <p className="hp-body mt-4 text-brand-navy">{testimonials.subtitle}</p>}
            </div>
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.items.map((item, idx) => (
                <figure key={idx} className="hp-card bg-brand-mint p-8">
                  <div className="mb-4 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-brand text-brand-ink" />
                    ))}
                  </div>
                  <blockquote className="text-lg leading-relaxed text-brand-navy">&ldquo;{item.quote}&rdquo;</blockquote>
                  <figcaption className="mt-5">
                    <p className="font-semibold text-brand-navy">{item.name}</p>
                    {item.role && <p className="text-sm text-brand-muted">{item.role}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <CtaBanner cta={content.cta} onAuth={openAuth} />
      </main>

      <SiteFooter />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </div>
  );
};

export default LandingPage;
